import React from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Info,
  ListChecks,
  Plus,
  RefreshCw,
  Save,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { VendorTable } from "@/components/VendorTable";
import { VendorCards } from "@/components/VendorCards";
import { summarize } from "@/lib/validation";

const Chip = ({ tone = "neutral", icon: Icon, label, value, testId }) => {
  const tones = {
    neutral: "border-border bg-secondary text-secondary-foreground",
    good: "border-success-border bg-success-surface text-success-text",
    bad: "border-danger-border bg-danger-surface text-danger-text",
    warn: "border-warning-border bg-warning-surface text-warning-text",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${tones[tone]}`}
      data-testid={testId}
    >
      {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden="true" /> : null}
      <span className="vde-code">{value}</span>
      <span className="font-normal">{label}</span>
    </span>
  );
};

export const ResultsPanel = ({
  rows,
  batch,
  dirty,
  saving,
  reextracting,
  queuing,
  onCellChange,
  onDeleteRow,
  onAddRow,
  onExport,
  onSave,
  onReextract,
  onSendToQueue,
}) => {
  const s = summarize(rows);

  return (
    <section className="space-y-3" data-testid="results-panel">
      <div className="vde-card p-3 sm:p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone="neutral" icon={Users} value={s.total} label="vendors" testId="vendors-found-count" />
            <Chip tone="good" icon={CheckCircle2} value={s.valid} label="valid" testId="valid-rows-count" />
            <Chip tone="bad" icon={AlertTriangle} value={s.invalid} label="invalid" testId="invalid-rows-count" />
            {s.warnings ? (
              <Chip tone="warn" icon={Info} value={s.warnings} label="to check" testId="warning-rows-count" />
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 bg-card"
              onClick={onAddRow}
              data-testid="add-row-button"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add row
            </Button>
            {batch?.id ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 bg-card"
                onClick={onReextract}
                disabled={reextracting}
                data-testid="reextract-button"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${reextracting ? "animate-spin" : ""}`}
                  aria-hidden="true"
                />
                Re-extract
              </Button>
            ) : null}
            {batch?.id ? (
              <Button
                variant={dirty ? "default" : "outline"}
                size="sm"
                className={`gap-1.5 ${dirty ? "" : "bg-card"}`}
                onClick={onSave}
                disabled={saving || !dirty}
                data-testid="save-batch-button"
              >
                <Save className="h-3.5 w-3.5" aria-hidden="true" />
                {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
              </Button>
            ) : null}
            {batch?.id && onSendToQueue ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 border-primary bg-card text-primary hover:bg-primary/5"
                onClick={onSendToQueue}
                disabled={queuing}
                data-testid="send-to-queue-button"
              >
                <ListChecks className="h-3.5 w-3.5" aria-hidden="true" />
                {queuing ? "Sending…" : "Send to queue"}
              </Button>
            ) : null}
            <Button
              size="sm"
              className="gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={onExport}
              data-testid="export-csv-button"
            >
              <Download className="h-3.5 w-3.5" aria-hidden="true" /> Export CSV
            </Button>
          </div>
        </div>

        {s.invalid > 0 ? (
          <p
            className="mt-3 flex items-start gap-1.5 rounded-lg border border-danger-border bg-danger-surface px-3 py-2 text-xs text-danger-text"
            data-testid="invalid-banner"
          >
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {s.invalid} row{s.invalid > 1 ? "s" : ""} {s.invalid > 1 ? "have" : "has"} an invalid
            PAN or IFSC. Flagged cells are outlined in red &mdash; fix them before exporting.
          </p>
        ) : null}
        {s.invalid === 0 && s.incomplete > 0 ? (
          <p
            className="mt-3 flex items-start gap-1.5 rounded-lg border border-warning-border bg-warning-surface px-3 py-2 text-xs text-warning-text"
            data-testid="incomplete-banner"
          >
            <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {s.incomplete} row{s.incomplete > 1 ? "s" : ""} {s.incomplete > 1 ? "are" : "is"}{" "}
            missing a key field (company, PAN, account number or IFSC). Amber cells need your
            attention.
          </p>
        ) : null}
      </div>

      <div className="hidden md:block">
        <VendorTable rows={rows} onCellChange={onCellChange} onDeleteRow={onDeleteRow} />
        <p className="mt-2 text-[11px] text-muted-foreground">
          Click any cell to edit · arrow keys move · Enter edits · Esc cancels · PAN, GSTIN and IFSC
          auto-uppercase
        </p>
      </div>
      <div className="md:hidden">
        <VendorCards rows={rows} onCellChange={onCellChange} onDeleteRow={onDeleteRow} />
      </div>
    </section>
  );
};

export default ResultsPanel;
